import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, of, Observable } from 'rxjs';
import { filter, take, map, distinctUntilChanged } from 'rxjs/operators';
import { Family, FamilyData } from '@app/models/family';
import { Adulte } from '@app/models/adulte';
import { ApiCrudService } from './api-crud.service';
import { Consumer } from '@app/models/consumer';
import { ConsumerAdapter } from './adapter/consumer-adapter.service';

const storageKey = 'currentFamily';

@Injectable({
  providedIn: 'root'
})
export class FamilyService extends ApiCrudService<Family> {
  url = 'familles';
  familyUrlPrefix = 'familles/#idFamille#/';

  private currentFamilySubject = new BehaviorSubject<Family>(this.getStoredFamily());

  currentFamily$ = this.currentFamilySubject.asObservable();

  currentFamilyReadyOnce$ = this.currentFamily$.pipe(
    filter(f => !!f),
    take(1)
  );

  currentFamilyChange$ = this.currentFamily$.pipe(
    distinctUntilChanged((x, y) => x?.id === y?.id)
  );

  reservationsToPay$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  // Not used, but could be ...
  familySwitch$ = this.currentFamily$.pipe(filter(f => !!f));

  constructor(
    protected http: HttpClient,
    private consumerAdapter: ConsumerAdapter,
  ) {
    super();
  }

  // Subject get / update

  get currentFamily() {
    return this.currentFamilySubject.value;
  }

  setCurrentFamily(family) {
    this.currentFamilySubject.next(family);
    this.storeFamily(family);
    this.reservationsToPay$.next(0);
  }

  // Local storage

  getStoredFamily(): Family {
    const family = sessionStorage.getItem(storageKey);

    return family ? JSON.parse(family) : null;
  }

  clearStoredFamily() {
    sessionStorage.removeItem(storageKey);
    localStorage.removeItem(storageKey);
  }

  storeFamily(family: Family) {
    this.clearStoredFamily();

    if (family) {
      sessionStorage.setItem(storageKey, JSON.stringify(family));
    }
  }

  // API calls

  getAdultFamilies(idAdulte) {
    return this.http.get<Family[]>(`adultes/${idAdulte}/familles`);
  }

  getFormData(id, part?) {
    if (!id){
      return of({modeCreation:true})
    }
    part = part ? part : '';
    // const url = this.formUrl.replace('#idFamille#', id).replace('#formPart#', part);
    const url = this.getFamilyDependantUrl(id, 'form/' + part);
    return this.http.get(url, this.httpOptions);
  }

  create(data: Family | any) {
    return this.http.post(this.url, data, this.httpOptions);
  }

  update(data: Family | any, id?: number, part?: string) {
    part = part ? part : '';
    const url = this.url + (id ? '/' + id : '') + '/' + part ;
    return this.http.put(url, data, this.httpOptions);
  }

  getConjointFormData(id, part?) {
    if (!id){
      return of({modeCreation:true})
    }
    part = part ? part : '';
    const url = this.getCurrentFamilyDependantUrl('conjoint/' + part);
    return this.http.get(url, this.httpOptions);
  }

  createConjoint(data: Adulte | any) {
    const url = this.getCurrentFamilyDependantUrl('conjoint');
    return this.http.post(url, data, this.httpOptions);
  }

  updateConjoint(data: Adulte | any, part?: string) {
    part = part ? part : '';
    const url = this.getCurrentFamilyDependantUrl('conjoint/' + part);
    return this.http.put(url, data, this.httpOptions);
  }

  defineCurrentFamily(families: Family[]) {
    const stored = this.getStoredFamily();

    families = families.sort((a: Family, b: Family) => (a.active === b.active) ? 0 : (a.active ? -1 : 1));

    if (stored) {
      // Refresh from fresh data
      this.setCurrentFamily(families.find(f => f.id === stored.id) || families[0]);
    } else {
      const defaultFamily = families.find(f => f.active) || families[0];
      this.setCurrentFamily(defaultFamily);
    }
  }

  getFamilyDependantUrl(idFamille, url) {
    return this.familyUrlPrefix.replace('#idFamille#', idFamille) + url;
  }

  getCurrentFamilyDependantUrl(url) {
    return this.getFamilyDependantUrl(this.currentFamily.id, url);
  }

  getFamilyData(id: number) {
    return this.http.get<FamilyData>(`familles/${id}`);
  }

  getFamilyVariables(idFamily) {
    return this.http.get(`/familles/${idFamily}/summary`);
  }

  getFamilyConsumers(family: Family | number): Observable<Consumer[]> {
    const id = typeof family === 'object' ? family.id : family;

    return this.http.get<Consumer[]>(`familles/${id}/usagers`).pipe(
      // Adapt
      map((data: []) => data.map(item => this.consumerAdapter.adapt(item))),
      // Tri des Usagers par Age, puis par nom / prenom
      map((consumers: Consumer[]) => consumers.sort((c1, c2) => {
        if (c1.age.years < c2.age.years) {
          return -1;
        } else if (c1.age.years > c2.age.years) {
          return 1;
        } else {
          if (c1.prenom + c1.nom < c2.prenom + c2.nom) {
            return -1;
          }
        }
        return 0;
      }))
    );
  }
}
